import DesktopBar from '@dashboard/common/components/widgets/DesktopBar'
import DesktopDialogEmailCollectionStep from '@dashboard/common/components/widgets/DesktopDialogEmailCollectionStep'
import DesktopDialogIntro from '@dashboard/common/components/widgets/DesktopDialogIntro'
import { selectHotelWebsite } from 'accessors'
import {
  PrimaryRegularButton,
  SecondaryRegularButton
} from 'components/ReusableComponents/Button/RegularButtons'
import Dialog from 'components/ReusableComponents/Dialog'
import FileUploaderDialog from 'components/ReusableComponents/FileUploaderDialog'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import TextArea from 'components/ReusableComponents/ManageHotel/TextArea'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import useBrand from 'hooks/useBrand'
import useClickOut from 'hooks/useClickOut'
import { findNewWidgetFileUrl } from 'features/EmailEditor/emailImageUploadHelpers'
import { useEffect, useRef, useState } from 'react'
import { ChromePicker } from 'react-color'
import { useSelector } from 'react-redux'
import { useToasts } from 'react-toast-notifications/dist/ToastProvider'
import widgetsApi from 'services/api/widgets'

import styles from './ConfigureWidgets.module.scss'
import { ReactComponent as LinkNewTabIcon } from './linkNewTab.svg'
import placeholder from './placeholder.jpg'

const previewTabs = {
  email: 'email',
  phone: 'phone'
}

const previewTabNames = {
  email: 'Email signup',
  phone: 'Phone signup'
}

function getRGB(c) {
  return parseInt(c, 16) || c
}

function getsRGB(c) {
  return getRGB(c) / 255 <= 0.03928
    ? getRGB(c) / 255 / 12.92
    : Math.pow((getRGB(c) / 255 + 0.055) / 1.055, 2.4)
}

function getLuminance(hexColor) {
  return (
    0.2126 * getsRGB(hexColor.substr(1, 2)) +
    0.7152 * getsRGB(hexColor.substr(3, 2)) +
    0.0722 * getsRGB(hexColor.substr(-2))
  )
}

function getContrast(f, b) {
  const L1 = getLuminance(f)
  const L2 = getLuminance(b)
  return (Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05)
}

function getTextColor(bgColor) {
  const whiteContrast = getContrast(bgColor, '#ffffff')
  const blackContrast = getContrast(bgColor, '#000000')

  return whiteContrast > blackContrast ? '#ffffff' : '#000000'
}

export const ColorInput = ({ title, defaultColor = '#FFFFFF', onChange, height }) => {
  const [color, setColor] = useState(defaultColor)

  const [isColorPickerVisible, setIsColorPickerVisible] = useState(false)

  const colorPickerRef = useRef()

  const [dialogX, setDialogX] = useState()
  const [dialogY, setDialogY] = useState()

  function handleClick(e) {
    setIsColorPickerVisible(true)
    setDialogX(e.clientX)
    setDialogY(e.clientY)
  }

  useClickOut(colorPickerRef, () => setIsColorPickerVisible(false))

  return (
    <div className={styles.colorPickerHolder}>
      <p className={styles.colorPickerTitle}>{title}</p>
      <div
        className={styles.colorPreviewBox}
        style={{ backgroundColor: color, height }}
        onClick={handleClick}
      >
        <p className={styles.selectedHexColor} style={{ color: getTextColor(color) }}>
          {color?.toUpperCase()}
        </p>
      </div>
      {isColorPickerVisible && (
        <div
          className={styles.colorPickerWrapper}
          ref={colorPickerRef}
          style={{ top: dialogY, left: dialogX }}
        >
          <ChromePicker
            disableAlpha={true}
            color={color}
            onChange={({ hex }) => {
              setColor(hex)
              onChange(hex)
            }}
          />
        </div>
      )}
    </div>
  )
}

export const ImageFileInput = ({
  defaultUrl,
  title,
  kind,
  backgroundSize = 'cover',
  onChange,
  minHeight,
  minWidth
}) => {
  const [previewUrl, setPreviewUrl] = useState(defaultUrl || '')
  const pendingUploadUrlRef = useRef('')
  const lastUploadedUrlRef = useRef('')
  const knownWidgetFilesRef = useRef([])
  const [isUploaderVisible, setIsUploaderVisible] = useState(false)

  const [isUploadingFile, setIsUploadingFile] = useState(false)

  const [dialogX, setDialogX] = useState()
  const [dialogY, setDialogY] = useState()

  useEffect(() => {
    const normalizedDefault = `${defaultUrl || ''}`.trim()
    const pendingUpload = `${pendingUploadUrlRef.current || ''}`.trim()
    const lastUploaded = `${lastUploadedUrlRef.current || ''}`.trim()

    if (!normalizedDefault) {
      if (pendingUpload) {
        setPreviewUrl(pendingUpload)
        return
      }

      if (lastUploaded) {
        setPreviewUrl(lastUploaded)
        return
      }

      setPreviewUrl('')
      return
    }

    if (normalizedDefault === pendingUpload) {
      pendingUploadUrlRef.current = ''
    }

    setPreviewUrl(normalizedDefault)
  }, [defaultUrl])

  function pollForNewWidgetFileUrl(uploadKind, knownUrls, attemptsLeft = 10) {
    return widgetsApi.files.get(uploadKind).then(recent => {
      const files = Array.isArray(recent) ? recent.filter(Boolean) : []
      const newUrl =
        knownUrls.length > 0 ? findNewWidgetFileUrl(knownUrls, files) : ''

      if (newUrl || attemptsLeft <= 1) {
        return newUrl
      }

      return new Promise(resolve => {
        setTimeout(() => {
          resolve(pollForNewWidgetFileUrl(uploadKind, knownUrls, attemptsLeft - 1))
        }, 600)
      })
    })
  }

  function onData() {
    setIsUploadingFile(true)
  }

  function onUpdateFiles() {
    setIsUploadingFile(true)
  }

  function applyUploadedUrl(url) {
    if (!url) {
      return
    }

    pendingUploadUrlRef.current = url
    lastUploadedUrlRef.current = url
    setPreviewUrl(url)
    onChange(url)
    setIsUploaderVisible(false)
  }

  function onLoadFile(res) {
    setIsUploadingFile(false)

    let data = null

    try {
      data = JSON.parse(res)
    } catch {
      return
    }

    const url = `${data?.result ?? data?.Result ?? ''}`.trim()
    const isSuccess = data?.isSuccess ?? data?.IsSuccess

    if (url) {
      applyUploadedUrl(url)
      return
    }

    if (isSuccess) {
      pollForNewWidgetFileUrl(kind, knownWidgetFilesRef.current)
        .then(fallbackUrl => {
          applyUploadedUrl(fallbackUrl)
        })
        .catch(() => {})
    }
  }

  function onClickRecentFile(url) {
    pendingUploadUrlRef.current = url
    lastUploadedUrlRef.current = url
    setPreviewUrl(url)
    onChange(url)
    setTimeout(() => setIsUploaderVisible(false), 10)
  }

  function onClickOut() {
    if (!isUploadingFile) {
      setIsUploaderVisible(false)
    }
  }

  function handleClick(e) {
    setIsUploaderVisible(true)
    setDialogX(e.clientX)
    setDialogY(e.clientY)
  }

  return (
    <div className={styles.imagePreviewHolder}>
      <p className={styles.imageTitle}>{title}</p>
      <div
        className={styles.imagePreviewBox}
        style={{
          backgroundImage: `url(${previewUrl || placeholder})`,
          backgroundColor: '#292121',
          backgroundSize
        }}
        onClick={handleClick}
      ></div>
      {isUploaderVisible && (
        <FileUploaderDialog
          onClickout={onClickOut}
          onLoad={onLoadFile}
          onUpdateFiles={onUpdateFiles}
          onClickRecentFile={onClickRecentFile}
          onRecentFilesReady={files => {
            knownWidgetFilesRef.current = files
          }}
          onData={onData}
          kind={kind}
          refreshRecentFilesOnUpload
          allowImageValidateSize={true}
          imageValidateSizeMinHeight={minHeight}
          imageValidateSizeMinWidth={minWidth}
          top={dialogY}
          left={dialogX}
        />
      )}
    </div>
  )
}

export const widgetNames = {
  TopBar: 'Top Bar',
  BottomBar: 'Bottom Bar',
  InstantPopup: 'Instant Popup',
  BeforeYouLeavePopup: 'Before You Leave Popup'
}

export function getWidgetProperties(colors = {}) {
  const primaryColor = colors.primary || '#FFFFFF'
  const actionColor = colors.action || '#0079ff'

  return {
    InstantPopup: {
      popupTitle: {
        name: 'Pop-up title',
        type: 'text',
        value: 'Sign up for texts to receive offers, deals & stay in the know!'
      },
      popupDescription: {
        name: 'Pop-up description',
        type: 'longText',
        value:
          'Sign up to receive exclusive discounts and special offers available only to our direct bookers.'
      },
      popupButtonText: {
        name: 'Pop-up button text',
        type: 'text',
        value: 'Get exclusive offer'
      },
      backgroundImageUrl:
        'https://dev-cdn.savibook.com/widget/08d70c6e-a338-7b41-f362-8ca7ac7799dc/Background/vojtech-bruzek-Yrxr3bsPdS0-unsplash-4.jpg',
      pagesToHide: []
    },
    BeforeYouLeavePopup: {
      popupTitle: {
        name: 'Pop-up title',
        type: 'text',
        value: 'Sign up for texts to receive offers, deals & stay in the know!'
      },
      popupDescription: {
        name: 'Pop-up description',
        type: 'longText',
        value:
          'Sign up to receive exclusive discounts and special offers available only to our direct bookers.'
      },
      popupButtonText: {
        name: 'Pop-up button text',
        type: 'text',
        value: 'Get exclusive offer'
      },
      backgroundImageUrl:
        'https://dev-cdn.savibook.com/widget/08d70c6e-a338-7b41-f362-8ca7ac7799dc/Background/vojtech-bruzek-Yrxr3bsPdS0-unsplash-4.jpg',
      pagesToHide: []
    },
    TopBar: {
      barTitle: {
        name: 'Bar title',
        type: 'text',
        value: 'Book direct for an exclusive discount'
      },
      barButtonText: {
        name: 'Bar button text',
        type: 'text',
        value: 'Claim offer'
      },
      backgroundColor: {
        name: 'Bar background color',
        type: 'color',
        value: primaryColor
      },
      barButtonColor: {
        name: 'Bar button color',
        type: 'color',
        value: actionColor
      },
      popupTitle: {
        name: 'Pop-up title',
        type: 'text',
        value: 'Sign up for texts to receive offers, deals & stay in the know!'
      },
      popupDescription: {
        name: 'Pop-up description',
        type: 'longText',
        value:
          'Sign up to receive exclusive discounts and special offers available only to our direct bookers.'
      },
      popupButtonText: {
        name: 'Pop-up button text',
        type: 'text',
        value: 'Get exclusive offer'
      },
      backgroundImageUrl:
        'https://dev-cdn.savibook.com/widget/08d70c6e-a338-7b41-f362-8ca7ac7799dc/Background/vojtech-bruzek-Yrxr3bsPdS0-unsplash-4.jpg',
      pagesToHide: []
    },
    BottomBar: {
      barTitle: {
        name: 'Bar title',
        type: 'text',
        value: 'Book direct for an exclusive discount'
      },
      barButtonText: {
        name: 'Bar button text',
        type: 'text',
        value: 'Claim offer'
      },
      backgroundColor: {
        name: 'Bar background color',
        type: 'color',
        value: primaryColor
      },
      barButtonColor: {
        name: 'Bar button color',
        type: 'color',
        value: actionColor
      },
      popupTitle: {
        name: 'Pop-up title',
        type: 'text',
        value: 'Sign up for texts to receive offers, deals & stay in the know!'
      },
      popupDescription: {
        name: 'Pop-up description',
        type: 'longText',
        value:
          'Sign up to receive exclusive discounts and special offers available only to our direct bookers.'
      },
      popupButtonText: {
        name: 'Pop-up button text',
        type: 'text',
        value: 'Get exclusive offer'
      },
      backgroundImageUrl:
        'https://dev-cdn.savibook.com/widget/08d70c6e-a338-7b41-f362-8ca7ac7799dc/Background/vojtech-bruzek-Yrxr3bsPdS0-unsplash-4.jpg',
      pagesToHide: []
    }
  }
}

const SingleWidgetConfig = ({
  widgetType,
  onSave,
  onClose,
  widgetsData,
  hotelName,
  textMarketingUrlKey,
  brandColors
}) => {
  const widgetDefaults = getWidgetProperties(brandColors)
  const [data, setData] = useState({
    ...widgetDefaults[widgetType],
    ...widgetsData.find(w => w.kind === widgetType)?.data,
    hotelName,
    textMarketingUrlKey
  })

  const [isSavingDraft, setIsSavingDraft] = useState(false)

  const websiteUrl = useSelector(selectHotelWebsite)

  function saveDraftAndGoToWebsite() {
    setIsSavingDraft(true)
    widgetsApi.list
      .post({
        isDraft: true,
        data: widgetsData.map(w => ({
          ...w,
          data: w.kind === widgetType ? JSON.stringify(data) : JSON.stringify(w.data)
        }))
      })
      .then(() => {
        setIsSavingDraft(false)
        const landingPageUrl = websiteUrl

        const url = new URL(landingPageUrl)
        url.searchParams.append('widgetPreviewMode', 'true')
        window.open(url.href, '_blank')
      })
  }

  return (
    <>
      <Dialog>
        <div className={styles.dialogWrapper}>
          <div className={styles.dialog}>
            <div className={styles.dialogContent}>
              <div className={styles.singleWidgetForm}>
                {(widgetType === 'TopBar' || widgetType === 'BottomBar') && (
                  <div className={styles.section}>
                    <p className={styles.sectionTitle}>{widgetNames[widgetType]}</p>
                    <div className={styles.inputHolder}>
                      <TextInput
                        label={'Title'}
                        className={styles.input}
                        value={data.barTitle.value}
                        onChange={e =>
                          setData({
                            ...data,
                            barTitle: { ...data.barTitle, value: e.target.value }
                          })
                        }
                      />
                    </div>
                    <div className={styles.inputHolder}>
                      <TextInput
                        label={'Button text'}
                        className={styles.input}
                        value={data.barButtonText.value}
                        onChange={e =>
                          setData({
                            ...data,
                            barButtonText: {
                              ...data.barButtonText,
                              value: e.target.value
                            }
                          })
                        }
                      />
                    </div>
                    <div className={styles.colorInputHolder}>
                      <ColorInput
                        title={data.backgroundColor.name}
                        defaultColor={data.backgroundColor.value}
                        height={40}
                        onChange={color =>
                          setData({
                            ...data,
                            backgroundColor: { ...data.backgroundColor, value: color }
                          })
                        }
                      />
                    </div>
                    <div className={styles.colorInputHolder}>
                      <ColorInput
                        title={data.barButtonColor.name}
                        defaultColor={data.barButtonColor.value}
                        height={40}
                        onChange={color =>
                          setData({
                            ...data,
                            barButtonColor: { ...data.barButtonColor, value: color }
                          })
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className={styles.dialogFooter}>
              <SecondaryRegularButton onClick={() => onClose()}>
                Discard
              </SecondaryRegularButton>
              <PrimaryRegularButton onClick={() => onSave(widgetType, data)}>
                Save changes
              </PrimaryRegularButton>
            </div>
          </div>
          <div className={styles.previewBox}>
            <div className={styles.previewHeader}>
              <p className={styles.previewTitle}>Preview</p>
              {/* <button className={styles.previewButton} onClick={saveDraftAndGoToWebsite}>
                {isSavingDraft ? (
                  <>
                    {' '}
                    <LinkNewTabIcon />
                    Generating preview...
                  </>
                ) : (
                  <>
                    <LinkNewTabIcon />
                    Preview on your website
                  </>
                )}
              </button> */}
            </div>
            <>
              <div>
                {widgetType === 'TopBar' && (
                  <div className={styles.previewTopBarHolder}>
                    <DesktopBar data={data} />
                  </div>
                )}
              </div>
              {widgetType === 'BottomBar' && (
                <div className={styles.previewBottomBarHolder}>
                  <DesktopBar data={data} />
                </div>
              )}
              {!widgetType && (
                <DesktopDialogIntro
                  data={data}
                  withOverlay={false}
                  isPhoneNumberValid={true}
                />
              )}
            </>
          </div>
        </div>
      </Dialog>
    </>
  )
}

const ConfigureWidgets = ({ onClose, widgetKind, hotelName, textMarketingUrlKey }) => {
  const [isLoading, setIsLoading] = useState(true)
  const [isSavingDraft, setIsSavingDraft] = useState(false)
  const [activePreviewTab, setActivePreviewTab] = useState(previewTabs.email)

  const { colors: brandColors, logoUrl: brandLogoUrl } = useBrand()

  const { addToast } = useToasts()

  const [widgets, setWidgets] = useState(null)

  const popupWidget =
    widgets?.find(w =>
      ['TopBar', 'BottomBar', 'InstantPopup', 'BeforeYouLeavePopup'].includes(w.kind)
    ) || widgets?.[0]

  const backgroundImageUrl = popupWidget?.data?.backgroundImageUrl

  const logoUrl = popupWidget?.data?.logoUrl || brandLogoUrl

  const brandColor = popupWidget?.data?.brandColor || brandColors.action

  const popupTitle = popupWidget?.data?.popupTitle?.value ?? ''

  const popupDescription = popupWidget?.data?.popupDescription?.value ?? ''

  const popupButtonText = popupWidget?.data?.popupButtonText?.value ?? ''

  const websiteUrl = useSelector(selectHotelWebsite)

  function setBackgroundImageUrl(backgroundImageUrl) {
    if (!widgets?.length) return
    setWidgets(widgets.map(w => ({ ...w, data: { ...w.data, backgroundImageUrl } })))
  }

  function setLogoUrl(logoUrl) {
    if (!widgets?.length) return
    setWidgets(widgets.map(w => ({ ...w, data: { ...w.data, logoUrl } })))
  }

  function setBrandColor(brandColor) {
    if (!widgets?.length) return
    setWidgets(widgets.map(w => ({ ...w, data: { ...w.data, brandColor } })))
  }

  function setPopupTitle(popupTitle) {
    if (!widgets?.length) return
    setWidgets(
      widgets.map(w => ({
        ...w,
        data: { ...w.data, popupTitle: { ...w.data?.popupTitle, value: popupTitle } }
      }))
    )
  }

  function setPopupDescription(popupDescription) {
    if (!widgets?.length) return
    setWidgets(
      widgets.map(w => ({
        ...w,
        data: {
          ...w.data,
          popupDescription: { ...w.data?.popupDescription, value: popupDescription }
        }
      }))
    )
  }

  function setPopupButtonText(popupButtonText) {
    if (!widgets?.length) return
    setWidgets(
      widgets.map(w => ({
        ...w,
        data: {
          ...w.data,
          popupButtonText: { ...w.data?.popupButtonText, value: popupButtonText }
        }
      }))
    )
  }

  function onSaveWidgetData(widgetType, data) {
    setIsLoading(true)
    widgetsApi.list
      .post({
        data: widgets
          .map(w => (w.kind === widgetType ? { ...w, data } : w))
          .map(w => ({ ...w, data: JSON.stringify(w.data) }))
      })
      .then(() => {
        onClose()
        addToast('Widgets loaded successfully', { appearance: 'success' })
      })
      .catch(err => {
        addToast(`${err}`, {
          appearance: 'error',
          autoDismiss: true
        })
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  function saveDraftAndGoToWebsite() {
    setIsSavingDraft(true)
    widgetsApi.list
      .post({
        isDraft: true,
        data: widgets.map(w => ({ ...w, data: JSON.stringify(w.data) }))
      })
      .then(() => {
        setIsSavingDraft(false)
        const landingPageUrl = websiteUrl

        const url = new URL(landingPageUrl)
        url.searchParams.append('widgetPreviewMode', 'true')
        window.open(url.href, '_blank')
      })
      .catch(err => {
        addToast(`${err}`, {
          appearance: 'error',
          autoDismiss: true
        })
      })
      .finally(() => {
        setIsLoading(false)
      })
  }
  function saveChanges() {
    setIsLoading(true)
    widgetsApi.list
      .post({ data: widgets.map(w => ({ ...w, data: JSON.stringify(w.data) })) })
      .then(() => {
        onClose()
        addToast('Widgets loaded successfully', { appearance: 'success' })
      })
      .catch(err => {
        addToast(`${err}`, {
          appearance: 'error',
          autoDismiss: true
        })
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  useEffect(() => {
    setIsLoading(true)

    widgetsApi.list
      .get()
      .then(response => {
        const list = Array.isArray(response) ? response : []
        setWidgets(
          list.map(w => ({
            ...w,
            data: typeof w.data === 'string' ? JSON.parse(w.data || '{}') : w.data || {}
          }))
        )
      })
      .catch(() => {
        setWidgets([])
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [addToast])

  if (isLoading) {
    return (
      <Dialog>
        <LoadingIndicator white />
      </Dialog>
    )
  }

  if (widgetKind) {
    return (
      <SingleWidgetConfig
        widgetType={widgetKind}
        widgetsData={widgets ?? []}
        defaultData={widgets?.find(w => w.kind === widgetKind)?.data}
        onClose={onClose}
        onSave={onSaveWidgetData}
        textMarketingUrlKey={textMarketingUrlKey}
        hotelName={hotelName}
        brandColors={brandColors}
      />
    )
  }

  if (!widgets?.length) {
    return (
      <Dialog>
        <div className={styles.dialogWrapper}>
          <div className={styles.dialog}>
            <div className={styles.emptyState}>
              <svg
                className={styles.emptyStateIcon}
                viewBox="0 0 64 64"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden
              >
                <rect x="8" y="8" width="20" height="20" rx="4" stroke="currentColor" strokeWidth="2" fill="none" />
                <rect x="36" y="8" width="20" height="20" rx="4" stroke="currentColor" strokeWidth="2" fill="none" />
                <rect x="8" y="36" width="20" height="20" rx="4" stroke="currentColor" strokeWidth="2" fill="none" />
                <rect x="36" y="36" width="20" height="20" rx="4" stroke="currentColor" strokeWidth="2" fill="none" />
              </svg>
              <h3 className={styles.emptyStateTitle}>No widgets configured</h3>
              <p className={styles.emptyStateDescription}>
                Enable at least one widget from the Site abandonment table above to customize the sign up dialog.
              </p>
              <SecondaryRegularButton onClick={onClose}>Close</SecondaryRegularButton>
            </div>
          </div>
        </div>
      </Dialog>
    )
  }

  return (
    <Dialog>
      <div className={styles.dialogWrapper}>
        <div className={styles.dialog}>
          <div className={styles.dialogHeader}>
            <span className={styles.headerTitle}>Customize sign up dialog</span>
            <div className={styles.tabsHolder}>
              <div className={styles.tabs}>
                {Object.values(previewTabs).map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActivePreviewTab(tab)}
                    className={tab === activePreviewTab ? styles.activeTab : styles.tab}
                  >
                    <span className={styles.paginatorNo}>{previewTabNames[tab]}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className={styles.dialogContent}>
            <div className={styles.brandConfig}>
              <div className={styles.brandOptions}>
                <ImageFileInput
                  kind="Background"
                  title="Background image"
                  defaultUrl={backgroundImageUrl}
                  onChange={setBackgroundImageUrl}
                  minWidth={380}
                  minHeight={600}
                />
                <ImageFileInput
                  kind="Logo"
                  title="Logo"
                  backgroundSize="90%"
                  defaultUrl={logoUrl}
                  onChange={setLogoUrl}
                  minWidth={200}
                  minHeight={200}
                />
                <ColorInput
                  title="Brand color"
                  defaultColor={brandColor}
                  onChange={setBrandColor}
                />
              </div>
            </div>
            <div className={styles.singleWidgetForm}>
              <div className={styles.section}>
                <div className={styles.inputHolder}>
                  <TextInput
                    label={'Title'}
                    className={styles.input}
                    value={popupTitle}
                    onChange={e => setPopupTitle(e.target.value)}
                  />
                </div>
                <div className={styles.inputHolder}>
                  <TextArea
                    label="Description"
                    className={styles.input}
                    value={popupDescription}
                    onChange={e => setPopupDescription(e.target.value)}
                  />
                </div>
                <div className={styles.inputHolder}>
                  <TextInput
                    label="Button text"
                    className={styles.input}
                    value={popupButtonText}
                    onChange={e => setPopupButtonText(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className={styles.dialogFooter}>
            <SecondaryRegularButton onClick={onClose}>Discard</SecondaryRegularButton>
            <PrimaryRegularButton onClick={saveChanges}>
              Save and publish
            </PrimaryRegularButton>
          </div>
        </div>
        <div className={styles.previewBox}>
          <div className={styles.previewHeader}>
            <p className={styles.previewTitle}>Preview</p>
          </div>
          {activePreviewTab === previewTabs.email ? (
            <DesktopDialogEmailCollectionStep
              data={widgets?.find(w => w.kind === 'TopBar')?.data ?? getWidgetProperties(brandColors).TopBar}
              withOverlay={false}
              isEmailValid={true}
            />
          ) : (
            <DesktopDialogIntro
              data={widgets?.find(w => w.kind === 'TopBar')?.data ?? getWidgetProperties(brandColors).TopBar}
              withOverlay={false}
              isPhoneNumberValid={true}
            />
          )}
        </div>
      </div>
    </Dialog>
  )
}

export default ConfigureWidgets
